/** Canonical application components — keep keys in sync with Flutter catalog. */
export type ApplicationRequirementOption = {
  key: string;
  label: string;
  description: string;
};

export type ApplicationRequirementSpec = {
  key: string;
  label?: string;
};

export const APPLICATION_REQUIREMENT_OTHER = "other";

export const APPLICATION_REQUIREMENT_OPTIONS: ApplicationRequirementOption[] = [
  {
    key: "academic_exam",
    label: "National / regional exam results",
    description: "Certificates or transcripts from board exams",
  },
  {
    key: "standardized_test",
    label: "Standardized test scores",
    description: "SAT, ACT, GMAT, GRE, or similar",
  },
  {
    key: "statement_of_purpose",
    label: "Statement of purpose",
    description: "Essay explaining goals and motivation",
  },
  {
    key: "personal_statement",
    label: "Personal statement",
    description: "Personal narrative or reflective essay",
  },
  {
    key: "recommendation_letter",
    label: "Letter of recommendation",
    description: "Reference letter from a teacher or mentor",
  },
  {
    key: "community_impact",
    label: "Community impact / volunteering",
    description: "Service and volunteering record",
  },
  {
    key: "activity",
    label: "Extracurricular / work experience",
    description: "Research, internships, sports, arts, or hobbies",
  },
  {
    key: APPLICATION_REQUIREMENT_OTHER,
    label: "Other",
    description: "Custom requirement — specify what you need",
  },
];

export function requirementLabel(spec: ApplicationRequirementSpec): string {
  if (spec.key === APPLICATION_REQUIREMENT_OTHER) {
    const custom = spec.label?.trim();
    if (custom) return custom;
    return "Other";
  }
  return (
    APPLICATION_REQUIREMENT_OPTIONS.find((o) => o.key === spec.key)?.label ||
    spec.label ||
    spec.key
  );
}

export function parseApplicationRequirements(raw: unknown): ApplicationRequirementSpec[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): ApplicationRequirementSpec | null => {
      if (typeof item === "string") {
        const text = item.trim();
        if (!text) return null;
        const known = APPLICATION_REQUIREMENT_OPTIONS.find((o) => o.key === text);
        if (known) return { key: known.key };
        return { key: APPLICATION_REQUIREMENT_OTHER, label: text };
      }
      if (item && typeof item === "object") {
        const row = item as Record<string, unknown>;
        const key = String(row.key ?? row.id ?? "").trim();
        const label = row.label ?? row.text ?? row.name;
        if (!key) {
          return {
            key: APPLICATION_REQUIREMENT_OTHER,
            label: label != null ? String(label) : "Other",
          };
        }
        return {
          key,
          label: label != null ? String(label) : undefined,
        };
      }
      return null;
    })
    .filter((x): x is ApplicationRequirementSpec => Boolean(x));
}

export function serializeApplicationRequirements(
  specs: ApplicationRequirementSpec[]
): ApplicationRequirementSpec[] {
  return specs
    .filter((s) => s.key.trim())
    .map((s) =>
      s.key === APPLICATION_REQUIREMENT_OTHER && s.label?.trim()
        ? { key: s.key, label: s.label.trim() }
        : { key: s.key }
    );
}
